import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';

const DATE_FORMAT = 'MM/dd/yyyy';
const EXACT_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

export interface DateInputProps {
  label: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  id: string;
  hasError: boolean;
  setDateIsFuture: (val: boolean) => void;
  setDateNotCurrent: (val: boolean) => void;
  setDateIsInvalid: (val: boolean) => void;
  onFocus?: () => void;
}

export function DateInput({
  label,
  value,
  onChange,
  id,
  hasError,
  setDateIsFuture,
  setDateNotCurrent,
  setDateIsInvalid,
  onFocus,
}: DateInputProps) {
  const [inputValue, setInputValue] = useState(
    value ? format(value, DATE_FORMAT) : ''
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPosRef = useRef<number | null>(null);

  useEffect(() => {
    setInputValue(value ? format(value, DATE_FORMAT) : '');
  }, [value]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || cursorPosRef.current == null) return;
    el.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
    cursorPosRef.current = null;
  }, [inputValue]);

  function clearErrors() {
    setDateIsFuture(false);
    setDateNotCurrent(false);
    setDateIsInvalid(false);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const rawValue = input.value;
    const cursorPos = input.selectionStart ?? rawValue.length;

    const digitsBeforeCursor = rawValue
      .slice(0, cursorPos)
      .replace(/\D/g, '').length;

    const digits = rawValue.replace(/\D/g, '').slice(0, 8);

    let masked = digits;

    if (digits.length >= 2) {
      masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    if (digits.length >= 4) {
      masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    let newPos = digitsBeforeCursor;
    if (digitsBeforeCursor >= 2) {
      newPos += 1;
    }
    if (digitsBeforeCursor >= 4) {
      newPos += 1;
    }

    cursorPosRef.current = newPos;

    setInputValue(masked);

    // hide error states while user is typing
    if (hasError) {
      clearErrors();
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorStart = input.selectionStart ?? 0;
    const cursorEnd = input.selectionEnd ?? 0;

    // only handle single-caret edits, not selected ranges
    if (cursorStart !== cursorEnd) {
      return;
    }

    if (
      e.key === 'Backspace' &&
      cursorStart > 0 &&
      inputValue[cursorStart - 1] === '/'
    ) {
      e.preventDefault();

      const nextValue =
        inputValue.slice(0, cursorStart - 2) + inputValue.slice(cursorStart);

      cursorPosRef.current = cursorStart - 2;
      setInputValue(nextValue);

      if (hasError) {
        clearErrors();
      }
    }

    if (e.key === 'Delete' && inputValue[cursorStart] === '/') {
      e.preventDefault();

      const nextValue =
        inputValue.slice(0, cursorStart) + inputValue.slice(cursorStart + 2);

      cursorPosRef.current = cursorStart;
      setInputValue(nextValue);

      if (hasError) {
        clearErrors();
      }
    }
  };

  function validateDate() {
    if (!inputValue) {
      clearErrors();
      onChange(undefined);
      return;
    }

    if (!EXACT_DATE_REGEX.test(inputValue)) {
      setDateIsInvalid(true);
      setDateIsFuture(false);
      return;
    }

    const parsedDate = parse(inputValue, DATE_FORMAT, new Date());
    const isValidDate =
      isValid(parsedDate) && format(parsedDate, DATE_FORMAT) === inputValue;

    // don't pass bad dates
    if (!isValidDate) {
      setDateIsInvalid(true);
      setDateIsFuture(false);
      setDateNotCurrent(false);
      return;
    }

    const schoolYearDateRange = getSchoolYearDateRange();

    // pass real dates, even from a previous school year
    if (parsedDate < schoolYearDateRange.from) {
      setDateIsInvalid(false);
      setDateIsFuture(false);
      setDateNotCurrent(true);
      onChange(parsedDate);
      return;
    }

    // pass real dates, even if in future
    if (parsedDate > schoolYearDateRange.to) {
      setDateIsInvalid(false);
      setDateIsFuture(true);
      setDateNotCurrent(false);
      onChange(parsedDate);
      return;
    }

    clearErrors();
    onChange(parsedDate);
  }

  return (
    <div className="flex max-w-32 flex-1 flex-col">
      <label htmlFor={id} className="text-sm font-medium text-neutral-1100">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={validateDate}
        className={`mt-1 h-7 rounded-sm bg-white px-2 text-neutral-1100 ${
          hasError
            ? 'border-2 border-brand-cliffordRed'
            : 'border border-neutral-400'
        }`}
        placeholder="MM/DD/YYYY"
        aria-invalid={hasError}
      />
    </div>
  );
}
